import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/app/_module/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/app/_module/components/ui/alert-dialog';
import { cn } from '@/app/_module/lib/utils';
import { formatPhoneNumberDisplay } from '@/app/_module/utils/phoneUtils';
import { getTextingNumberActivation } from '@/app/_module/utils/textingNumberActivation';
import { Notice, Panel, Section, controlBorder, focusRing } from './primitives';

const stateNameOf = (p: any): string | null =>
  (typeof p?.state === 'string' ? p.state : p?.state?.name) || p?.stateName || null;

/**
 * Sole proprietor only: the one number the registration texts from. Changing it
 * costs weeks of undelivered texts while the carriers register the new number,
 * so the change sits behind a dialog with a required acknowledgement.
 */
export function TextingNumberSection({
  phoneNumbers,
  loading,
  selectedId,
  onSelect,
  swapping,
  confirmOpen,
  onConfirmOpenChange,
  acknowledged,
  onAcknowledge,
  onRequestChange,
  onConfirm,
}: {
  phoneNumbers: any[];
  loading: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
  swapping: boolean;
  confirmOpen: boolean;
  onConfirmOpenChange: (open: boolean) => void;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  onRequestChange: () => void;
  onConfirm: () => void;
}) {
  const current = phoneNumbers.find((p: any) => p?.campaignLinkStatus === 'linked');
  const others = phoneNumbers.filter((p: any) => p?.campaignLinkStatus !== 'linked');
  const activation = getTextingNumberActivation(current?.linkedToCampaignAt);
  const chosen = phoneNumbers.find((p: any) => p.id === selectedId);
  const place = stateNameOf(current);

  return (
    <Section
      id="a2p-texting-number"
      title="Texting number"
      description="Your registration sends texts from one number. Your other numbers still make calls and receive texts."
    >
      <Panel>
        <div className="px-4 py-4 sm:px-5 sm:py-5">
          <p className="text-xs text-neutral-500">Sending from</p>
          {current?.phoneNumber ? (
            <>
              <p className="mt-1 text-2xl font-semibold tabular-nums tracking-[-0.01em] text-neutral-900">
                {formatPhoneNumberDisplay(current.phoneNumber)}
              </p>
              {place && <p className="mt-0.5 text-sm text-neutral-500">{place}</p>}
            </>
          ) : (
            <p className="mt-1 text-sm font-medium text-neutral-900">
              {loading ? 'Loading your number…' : 'No texting number set'}
            </p>
          )}
          {activation && (
            <div className="mt-4">
              <Notice icon={Clock}>
                <strong>Activating with the carriers.</strong>{' '}
                {activation.daysSinceSwitch === 0
                  ? 'Changed today.'
                  : `Changed ${activation.daysSinceSwitch} day${activation.daysSinceSwitch === 1 ? '' : 's'} ago.`}{' '}
                It can take several days to go live, and texts sent before then may not be delivered.
              </Notice>
            </div>
          )}
        </div>

        {phoneNumbers.length > 1 && others.length > 0 && (
          <div className="border-t border-neutral-100 px-4 py-4 sm:px-5 sm:py-5">
            <label htmlFor="a2p-texting-number-select" className="text-sm font-medium text-neutral-900">
              Switch to another number
            </label>
            <p className="mt-1 text-pretty text-sm leading-relaxed text-neutral-500">
              Texting stops until the carriers register the new number, which can take up to 2-3 weeks. Switching back
              doesn&apos;t speed it up.
            </p>
            <div className="mt-3 flex flex-col gap-2.5 sm:flex-row">
              <select
                id="a2p-texting-number-select"
                value={selectedId}
                onChange={(e) => onSelect(e.target.value)}
                disabled={swapping}
                className={cn(
                  'h-11 w-full min-w-0 rounded-md border bg-white px-3 text-sm tabular-nums text-neutral-900 disabled:opacity-50 sm:flex-1',
                  controlBorder,
                  focusRing,
                )}
              >
                <option value="">Choose a number</option>
                {others.map((p: any) => {
                  const st = stateNameOf(p);
                  return (
                    <option key={p.id} value={p.id}>
                      {formatPhoneNumberDisplay(p.phoneNumber)}
                      {st ? ` · ${st}` : ''}
                    </option>
                  );
                })}
              </select>
              <Button
                variant="outline"
                onClick={onRequestChange}
                disabled={!selectedId || swapping}
                className={cn('h-11', focusRing)}
              >
                Change texting number
              </Button>
            </div>
          </div>
        )}
      </Panel>

      <AlertDialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-start gap-2.5 text-left !text-lg">
              <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0 text-amber-700" />
              Texting will stop for up to 2-3 weeks
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left text-sm leading-relaxed text-neutral-600">
                <p>
                  You&apos;re changing your texting number to{' '}
                  <strong className="font-medium tabular-nums text-neutral-900">
                    {formatPhoneNumberDisplay(chosen?.phoneNumber || '')}
                  </strong>
                  .
                </p>
                <p>
                  The carriers have to register the new number before they accept texts from it. That usually takes a
                  few days and can take up to 2-3 weeks. Until then:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Texts you send won&apos;t be delivered</li>
                  <li>Switching back won&apos;t make it faster</li>
                  <li>Calls and incoming texts keep working</li>
                </ul>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-amber-50 p-3 text-amber-800">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => onAcknowledge(e.target.checked)}
                    className={cn('mt-0.5 size-4 shrink-0 accent-amber-700', focusRing)}
                  />
                  <span>I understand my texting may not work for up to 2-3 weeks, and I want to change my number.</span>
                </label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={swapping} className={cn('h-11 sm:h-10', focusRing)}>
              Keep my current number
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
              disabled={!acknowledged || swapping}
              className={cn('h-11 bg-amber-700 text-white hover:bg-amber-800 sm:h-10', focusRing)}
            >
              {swapping ? 'Changing…' : 'Yes, change my number'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}
